import React, { forwardRef } from 'react'

import { Icon } from '../icon'
import { type IconProps } from '../icon/Icon.types'

type IconRef = React.ComponentRef<typeof Icon>
export type ButtonIconProps = Pick<IconProps, 'source' | 'as' | 'size' | 'color'> & {
    className?: string
    testID?: string
}

// Button-icon pixel scale (kept on the Button so icon sizes don't shift when
// the standalone `Icon` follows Gluestack's own size scale).
const BUTTON_ICON_PX: Record<string, number> = {
    '2xs': 12,
    'xs': 16,
    'sm': 20,
    'md': 24,
    'lg': 32,
    'xl': 40,
}
const resolveButtonIconPx = (size: IconProps['size']): number =>
    typeof size === 'number' ? size : BUTTON_ICON_PX[size ?? 'md']

/**
 * The glyph handed to `Button`'s `iconLeft` / `iconRight`.
 *
 * It carries no ink of its own: the Button clones it with the label's colour
 * from the Figma contract, and an explicit `color` or `className` here still
 * wins the merge.
 */
export const ButtonIcon = forwardRef<IconRef, ButtonIconProps>(
    ({ source, as: asProp, size, color, className, ...rest }, ref) => {
        const SvgComponent = asProp ?? source
        if (!SvgComponent) {
            return null
        }
        return (
            <Icon
                ref={ref}
                as={SvgComponent}
                size={resolveButtonIconPx(size)}
                color={color}
                className={className}
                {...rest}
            />
        )
    },
)
ButtonIcon.displayName = 'ButtonIcon'
